import { useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  ConfirmModal,
  LabInvoicePdfFrame,
  Loading,
  Modal,
  Pagination,
  showError,
  showSuccess,
} from "@common";
import {
  useLabInvoiceAttachments,
  useClaimLabInvoiceAttachment,
  useDiscardLabInvoiceAttachment,
  useRequestLabInvoiceAttachmentReview,
  useReviewLabInvoiceBreakdown,
  useRescanLabInvoiceAttachment,
  useRetryLabInvoiceAttachmentFromEmail,
  useUpdateLabInvoiceAttachmentAssignment,
  useUpdateLabInvoiceAttachmentPdfEdits,
  useUploadLabInvoice,
} from "@api/hooks";
import { getApiErrorMessage } from "@utils/apiErrorUtils";
import { formatMoney } from "@utils/format";
import { normalizeTypeForUi } from "@utils/utils";
import { getRoster } from "@utils/practitionerUtils";

const MAX_PDF_UPLOAD_BYTES = 2 * 1024 * 1024;
const LAB_INVOICE_STATUS_TABS = [
  { key: "all", label: "All" },
  { key: "pending", label: "Pending" },
  { key: "delivered", label: "Delivered" },
  { key: "applied", label: "Applied" },
];

function formatConfidencePercent(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "";
  const percent = numeric <= 1 ? numeric * 100 : numeric;
  return `${Number.isInteger(percent) ? percent.toFixed(0) : percent.toFixed(1)}%`;
}

function getLowConfidenceLabel(breakdown) {
  if (!breakdown?.low_confidence && !breakdown?.review_required) return "";
  const percent = formatConfidencePercent(breakdown.confidence);
  return percent ? `low ${percent}` : "low";
}

function getSuggestedAmount(attachment) {
  const breakdownTotal = (attachment.breakdowns || [])
    .filter((breakdown) => breakdown.status === "suggested")
    .reduce((total, breakdown) => total + Number(breakdown.total_amount || 0), 0);
  return Number(attachment.total_amount ?? attachment.extracted_lab_cost ?? breakdownTotal ?? 0);
}

// Applied invoices are locked (backend rejects re-assignment). Assigned-but-not
// -applied invoices can still be reassigned to a different clinic/date/practitioner.
function isAttachmentApplied(attachment) {
  return (
    attachment.lab_cost_status === "applied" ||
    Boolean(attachment.applied_report) ||
    (attachment.breakdowns || []).some((breakdown) => breakdown.status === "applied")
  );
}

function getReconciliationBadgeClass(status) {
  switch (status) {
    case "matched":
      return "bg-palette-emerald-muted text-palette-emerald-text";
    case "mismatch":
      return "bg-palette-rose-muted text-palette-rose-heading";
    default:
      // missing_total / manual_review and anything unexpected
      return "bg-palette-amber-muted text-palette-amber-text";
  }
}

function getMismatchDetail(attachment) {
  if (attachment.reconciliation_status !== "mismatch") return "";
  const invoiceTotal = attachment.total_amount;
  const breakdownTotal = attachment.breakdown_total_amount;
  const difference = attachment.breakdown_difference;
  const parts = [];

  if (invoiceTotal !== null && invoiceTotal !== undefined) {
    parts.push(`Invoice total ${formatMoney(invoiceTotal)}`);
  }
  if (breakdownTotal !== null && breakdownTotal !== undefined) {
    parts.push(`Breakdown total ${formatMoney(breakdownTotal)}`);
  }
  if (difference !== null && difference !== undefined) {
    parts.push(`Difference ${formatMoney(difference)}`);
  }

  return parts.join(" · ");
}

// True when the attachment carries a scanned lab-cost figure — including $0
// (e.g. warranty/remake at no charge), but not when there is no suggestion at
// all. Drives whether we render the amount badge.
function hasSuggestedAmount(attachment) {
  const hasSuggestedBreakdown = (attachment.breakdowns || []).some(
    (breakdown) => breakdown.status === "suggested",
  );
  return (
    hasSuggestedBreakdown ||
    attachment.extracted_lab_cost !== null ||
    attachment.total_amount !== null
  );
}

function getAssignBlockReason(attachment) {
  if (attachment.document_type === "lab_invoice_batch") {
    return "Batch PDFs cannot be delivered. Split it into one invoice per PDF and upload again.";
  }

  if (attachment.total_amount === null || attachment.total_amount === undefined) {
    return "Invoice total is missing. Edit the PDF total before delivery.";
  }

  const hasBreakdownAmount = (attachment.breakdowns || []).some(
    (breakdown) => breakdown.total_amount !== null && breakdown.total_amount !== undefined,
  );
  if (!hasBreakdownAmount) {
    return "Lab cost amount is missing. Add at least one cost breakdown before delivery.";
  }

  if (attachment.reconciliation_status === "mismatch") {
    return "Fix the cost breakdown mismatch before delivery.";
  }

  const hasUnreviewedLowConfidenceBreakdown = (attachment.breakdowns || []).some(
    (breakdown) => breakdown.review_required && !breakdown.reviewed_at,
  );
  if (hasUnreviewedLowConfidenceBreakdown) {
    return "Confirm all low-confidence breakdown rows before delivery.";
  }

  return "";
}

function withOptimisticReviewedBreakdowns(attachment, optimisticReviewedBreakdownIds) {
  if (!optimisticReviewedBreakdownIds.length) return attachment;
  const reviewedIds = new Set(optimisticReviewedBreakdownIds.map((id) => String(id)));
  return {
    ...attachment,
    breakdowns: (attachment.breakdowns || []).map((breakdown) => {
      if (!reviewedIds.has(String(breakdown.id))) return breakdown;
      return {
        ...breakdown,
        review_required: false,
        reviewed_at: breakdown.reviewed_at || "pending",
      };
    }),
  };
}

function getLabInvoiceEmptyMessage(status, hasSearch) {
  if (hasSearch) return "No lab invoices match this search for this date.";
  switch (status) {
    case "pending":
      return "No pending delivery lab invoices for this date.";
    case "delivered":
      return "No delivered lab invoices for this date.";
    case "applied":
      return "No applied lab invoices for this date.";
    default:
      return "No lab invoices for this date.";
  }
}

function getPractitionerLabel(attachment) {
  const breakdown = (attachment.breakdowns || []).find(
    (entry) => entry.matched_practitioner_name || entry.practitioner_name,
  );
  return (
    attachment.matched_practitioner_name ||
    attachment.practitioner_name ||
    breakdown?.matched_practitioner_name ||
    breakdown?.practitioner_name ||
    ""
  );
}

function getAssignedDateLabel(attachment) {
  if (attachment.assigned_date) return attachment.assigned_date;
  const breakdown = (attachment.breakdowns || []).find((entry) => entry.assigned_date);
  return breakdown?.assigned_date || "";
}

function getPractitionerName(practitioner) {
  return practitioner?.name || practitioner?.profile?.name || "";
}

function isActivePractitioner(practitioner) {
  return String(practitioner?.status || "active").toLowerCase() !== "inactive";
}

function canPractitionerUseLabCosts(practitioner, hiddenColumns = []) {
  const hidden = hiddenColumns.includes("lab_costs") || hiddenColumns.includes("lab");
  if (hidden) return false;
  // Only Dentists can be assigned lab costs. Previously this was `!== "HYG"`, which was
  // equivalent to "DDS only" back when DDS/HYG were the only types — but the newer types
  // (Area Manager, Receptionist, etc.) are also "not HYG" and were leaking in. Restrict to
  // DDS explicitly so the module behaves exactly as before (temporary hold on new types).
  return normalizeTypeForUi(practitioner?.practitioner_type) === "DDS";
}

function isDraftBreakdownId(id) {
  return String(id || "").startsWith("draft:");
}

function createDraftBreakdown(attachmentId) {
  return {
    id: `draft:${attachmentId}:${Date.now()}`,
    attachment: attachmentId,
    invoice_number: "",
    patient_name: "",
    description: "",
    service_date: "",
    invoice_date: "",
    amount: "",
    tax_amount: "",
    total_amount: "",
    currency: "CAD",
    status: "suggested",
    created_at: new Date().toISOString(),
  };
}

function getBreakdownDraft(breakdown) {
  return {
    invoice_number: breakdown.invoice_number || "",
    patient_name: breakdown.patient_name || "",
    description: breakdown.description || "",
    service_date: breakdown.service_date || "",
    invoice_date: breakdown.invoice_date || "",
    amount: breakdown.amount ?? "",
    tax_amount: breakdown.tax_amount ?? "",
    total_amount: breakdown.total_amount ?? "",
    currency: breakdown.currency || "CAD",
    reviewed: false,
  };
}

function getOrderedBreakdowns(attachment) {
  return [...(attachment.breakdowns || [])].sort((a, b) => {
    const aTime = a.created_at ? new Date(a.created_at).getTime() : 0;
    const bTime = b.created_at ? new Date(b.created_at).getTime() : 0;
    if (aTime !== bTime) return aTime - bTime;
    return Number(a.id || 0) - Number(b.id || 0);
  });
}

function cleanBreakdownDraft(draft) {
  const nullableDateFields = ["service_date", "invoice_date"];
  const nullableMoneyFields = ["amount", "tax_amount", "total_amount"];
  const data = { ...draft };

  nullableDateFields.forEach((field) => {
    if (!data[field]) data[field] = null;
  });
  nullableMoneyFields.forEach((field) => {
    if (data[field] === "") data[field] = null;
  });
  data.currency = (data.currency || "CAD").trim().toUpperCase();
  return data;
}

function isBlankBreakdownDraft(draft) {
  if (!draft) return true;
  return [
    "invoice_number",
    "patient_name",
    "description",
    "service_date",
    "invoice_date",
    "amount",
    "tax_amount",
    "total_amount",
  ].every((field) => String(draft[field] ?? "").trim() === "");
}

function isInteractiveTarget(target) {
  return Boolean(
    target?.closest?.('button, input, select, textarea, a, [data-no-row-preview="true"]'),
  );
}

function BreakdownEditorRow({
  breakdown,
  draft,
  canEdit,
  isSaving,
  isMarkedForRemoval,
  onChange,
  onToggleRemove,
  onReview,
  isReviewing,
}) {
  const isApplied = breakdown.status === "applied";
  const reviewRequired = Boolean(breakdown.review_required && !breakdown.reviewed_at);
  const canShowActions = canEdit || (reviewRequired && onReview);
  const confidenceLabel = getLowConfidenceLabel(breakdown);
  const gridColumnsClass = canShowActions
    ? "lg:grid-cols-[minmax(120px,0.8fr)_minmax(120px,0.8fr)_minmax(120px,0.7fr)_auto]"
    : "lg:grid-cols-[minmax(120px,0.8fr)_minmax(120px,0.8fr)_minmax(120px,0.7fr)]";

  function updateField(field, value) {
    onChange(breakdown.id, { ...draft, [field]: value });
  }

  return (
    <div
      className={`grid gap-2 border-t border-palette-slate-muted py-3 ${gridColumnsClass} ${
        isMarkedForRemoval ? "opacity-50" : ""
      }`}
    >
      <label className="text-xs font-medium text-palette-slate-badge">
        Patient
        <input
          className="mt-1 block w-full rounded border border-palette-slate-ring px-2 py-1 text-sm"
          value={draft?.patient_name || ""}
          disabled={!canEdit || isApplied || isSaving || isMarkedForRemoval}
          onChange={(event) => updateField("patient_name", event.target.value)}
        />
      </label>

      <label className="text-xs font-medium text-palette-slate-badge">
        Description
        <input
          className="mt-1 block w-full rounded border border-palette-slate-ring px-2 py-1 text-sm"
          value={draft?.description || ""}
          disabled={!canEdit || isApplied || isSaving || isMarkedForRemoval}
          onChange={(event) => updateField("description", event.target.value)}
        />
      </label>

      <label className="text-xs font-medium text-palette-slate-badge">
        <span className="flex items-center gap-1.5">
          Amount
          {confidenceLabel && (
            <span className="inline-flex rounded bg-palette-amber-muted px-1.5 py-0.5 text-[11px] font-semibold normal-case text-palette-amber-text">
              {confidenceLabel}
              {breakdown.reviewed_at ? " · reviewed" : ""}
            </span>
          )}
        </span>
        <input
          type="number"
          step="0.01"
          className="mt-1 block w-full rounded border border-palette-slate-ring px-2 py-1 text-sm"
          value={draft?.total_amount ?? ""}
          disabled={!canEdit || isApplied || isSaving || isMarkedForRemoval}
          onChange={(event) => updateField("total_amount", event.target.value)}
        />
      </label>

      {canShowActions && (
        <div className="flex items-end justify-end gap-2">
          {reviewRequired && onReview && (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={isApplied || isSaving || isMarkedForRemoval || isReviewing}
              title="Confirm this low-confidence row"
              onClick={() => onReview(breakdown.id)}
            >
              {isReviewing ? "Confirming..." : "Confirm"}
            </Button>
          )}
          {canEdit && (
            <Button
              type="button"
              variant={isMarkedForRemoval ? "secondary" : "danger"}
              size="sm"
              disabled={isApplied || isSaving}
              title={isMarkedForRemoval ? "Undo remove" : "Remove on save"}
              onClick={() => onToggleRemove(breakdown.id)}
            >
              {isMarkedForRemoval ? "+" : "-"}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

function InvoiceRow({
  attachment,
  clinicId,
  defaultDate,
  practitioners,
  hiddenColumns,
  canEdit,
  canRescan,
  isOpen,
  onTogglePdf,
  updateAssignment,
  onRequestReview,
  onAddBreakdown,
  onSavePdfEdits,
  onCancelPdfEdits,
  isSavingPdf,
  isRequestingReview,
  onDiscard,
  isDiscarding,
  onRescan,
  isRescanning,
  onRetryFromEmail,
  isRetrying,
  onReviewBreakdown,
  reviewingBreakdownIds,
}) {
  const [draft, setDraft] = useState({
    practitioner_id: attachment.practitioner_id ? String(attachment.practitioner_id) : "",
  });
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignDate, setAssignDate] = useState("");
  const [pdfEditing, setPdfEditing] = useState(false);
  const [totalDraft, setTotalDraft] = useState("");
  const [breakdownDrafts, setBreakdownDrafts] = useState({});
  const [breakdownsOpen, setBreakdownsOpen] = useState(false);
  const [removeBreakdownIds, setRemoveBreakdownIds] = useState([]);
  const suggestedAmount = getSuggestedAmount(attachment);
  const showAmount = hasSuggestedAmount(attachment);
  const practitionerLabel = getPractitionerLabel(attachment);
  const senderLabel = attachment.sender_name || attachment.sender_email || "unknown";
  const isScanning = attachment.ai_status === "scanning";
  const isApplied = isAttachmentApplied(attachment);
  const isAssigned = Boolean(attachment.assigned_date);
  const assignedDateLabel = getAssignedDateLabel(attachment);
  const reviewRequested = Boolean(attachment.review_requested_at && !attachment.review_resolved_at);
  const rowLocked = isApplied || reviewRequested;
  const assignBlockReason = getAssignBlockReason(attachment);
  const canAssignToReport = canEdit && !rowLocked && !assignBlockReason;
  const assignmentDisabled = isScanning || isRescanning || updateAssignment.isPending;
  const orderedBreakdowns = getOrderedBreakdowns(attachment);
  const mismatchDetail = getMismatchDetail(attachment);
  // PDF stored in GCS → re-run the AI scan on it. Not yet stored (e.g. upload
  // failed) → refetch the original attachment from the source email instead.
  const canViewPdf = attachment.storage_status === "uploaded";
  const canRetryFromEmail =
    !canViewPdf && Boolean(attachment.email) && Boolean(attachment.attachment_id);
  const selectedPractitionerInRoster = draft.practitioner_id
    ? practitioners.find(
        (practitioner) => String(practitioner.id) === String(draft.practitioner_id),
      )
    : null;
  const hasInactiveAssignedPractitioner = Boolean(
    attachment.practitioner_id &&
    (!selectedPractitionerInRoster || !isActivePractitioner(selectedPractitionerInRoster)),
  );
  const hasLabCostDisabledPractitioner = Boolean(
    attachment.practitioner_id &&
    selectedPractitionerInRoster &&
    !canPractitionerUseLabCosts(selectedPractitionerInRoster, hiddenColumns),
  );
  const assignablePractitioners = practitioners.filter(
    (practitioner) =>
      isActivePractitioner(practitioner) && canPractitionerUseLabCosts(practitioner, hiddenColumns),
  );
  const visibleBreakdowns = orderedBreakdowns.filter(
    (breakdown) => !removeBreakdownIds.includes(breakdown.id),
  );

  useEffect(() => {
    if (!pdfEditing) {
      setTotalDraft(
        attachment.total_amount !== null && attachment.total_amount !== undefined
          ? String(attachment.total_amount)
          : "",
      );
    }
  }, [attachment.total_amount, pdfEditing]);

  useEffect(() => {
    setBreakdownDrafts((current) => {
      const next = {};
      let changed = false;
      orderedBreakdowns.forEach((breakdown) => {
        if (current[breakdown.id]) {
          next[breakdown.id] = current[breakdown.id];
        } else {
          next[breakdown.id] = getBreakdownDraft(breakdown);
          changed = true;
        }
      });
      if (Object.keys(current).length !== orderedBreakdowns.length) changed = true;
      return changed ? next : current;
    });
  }, [orderedBreakdowns]);

  function openAssign() {
    if (assignmentDisabled) {
      showError("Wait for the scan to finish before delivery.");
      return;
    }
    if (assignBlockReason) {
      showError(assignBlockReason);
      return;
    }
    if (!draft.practitioner_id) {
      showError("Select a practitioner before delivery.");
      return;
    }
    setAssignDate(attachment.assigned_date || defaultDate || "");
    setAssignOpen(true);
  }

  async function assign() {
    if (!assignDate) return;
    if (assignmentDisabled) {
      showError("Wait for the scan to finish before delivery.");
      return;
    }
    if (assignBlockReason) {
      showError(assignBlockReason);
      return;
    }
    if (!draft.practitioner_id) {
      showError("Select a practitioner before delivery.");
      return;
    }
    try {
      const payload = {
        clinic: clinicId,
        assigned_date: assignDate,
        practitioner_id: Number(draft.practitioner_id),
      };
      await updateAssignment.mutateAsync({ attachmentId: attachment.id, data: payload });
      showSuccess(isAssigned ? "Lab invoice delivery updated." : "Lab invoice delivered.");
      setAssignOpen(false);
    } catch (err) {
      showError(getApiErrorMessage(err, "Failed to update lab invoice delivery."));
    }
  }

  function discard() {
    onDiscard(attachment.id, attachment.original_filename, {
      delivered: Boolean(assignedDateLabel),
      applied: isApplied,
    });
  }

  function requestReview() {
    onRequestReview(attachment.id, attachment.original_filename);
  }

  function openPdfEdit() {
    setTotalDraft(
      attachment.total_amount !== null && attachment.total_amount !== undefined
        ? String(attachment.total_amount)
        : "",
    );
    setBreakdownDrafts(
      Object.fromEntries(
        orderedBreakdowns.map((breakdown) => [breakdown.id, getBreakdownDraft(breakdown)]),
      ),
    );
    setRemoveBreakdownIds([]);
    setBreakdownsOpen(true);
    setPdfEditing(true);
  }

  function cancelPdfEdit() {
    setRemoveBreakdownIds([]);
    setPdfEditing(false);
    onCancelPdfEdits(attachment.id);
  }

  function updateBreakdownDraft(id, nextDraft) {
    setBreakdownDrafts((current) => ({ ...current, [id]: nextDraft }));
  }

  function toggleRemoveBreakdown(id) {
    if (!pdfEditing) return;
    setRemoveBreakdownIds((current) =>
      current.includes(id) ? current.filter((item) => item !== id) : [...current, id],
    );
  }

  async function savePdfEdits() {
    const value = String(totalDraft || "").trim();
    if (!value) {
      showError("PDF total amount is required.");
      return;
    }
    const hasRemainingBreakdownAmount = visibleBreakdowns.some((breakdown) => {
      const draftValue =
        breakdownDrafts[breakdown.id]?.total_amount ?? getBreakdownDraft(breakdown).total_amount;
      return String(draftValue ?? "").trim() !== "";
    });
    if (attachment.assigned_date && !hasRemainingBreakdownAmount) {
      showError("Delivered lab invoices must have at least one cost breakdown before saving.");
      return;
    }
    const hasUnreviewedLowConfidenceRow = visibleBreakdowns.some((breakdown) => {
      if (!breakdown.review_required || breakdown.reviewed_at) return false;
      return !breakdownDrafts[breakdown.id]?.reviewed;
    });
    if (hasUnreviewedLowConfidenceRow) {
      showError("Review all low-confidence breakdown rows before saving.");
      return;
    }
    try {
      await onSavePdfEdits({
        attachmentId: attachment.id,
        totalAmount: value,
        breakdowns: visibleBreakdowns.map((breakdown) => ({
          id: breakdown.id,
          data: cleanBreakdownDraft(breakdownDrafts[breakdown.id] || getBreakdownDraft(breakdown)),
        })),
        deleteBreakdownIds: removeBreakdownIds
          .filter((id) => !isDraftBreakdownId(id))
          .map((id) => Number(id)),
        discardDraftIds: removeBreakdownIds.filter((id) => isDraftBreakdownId(id)),
      });
      setRemoveBreakdownIds([]);
      setPdfEditing(false);
    } catch {
      // Parent shows the API error; keep the editor open so the user can fix it.
    }
  }

  function handleRowClick(event) {
    if (isInteractiveTarget(event.target)) return;
    onTogglePdf(attachment.id);
  }

  return (
    <div
      className={`overflow-hidden rounded-md border bg-white shadow-sm transition-shadow hover:shadow-md ${
        isOpen
          ? "border-palette-indigo-ring ring-1 ring-palette-indigo-hover"
          : "border-palette-slate-divider"
      }`}
      role="button"
      tabIndex={0}
      onClick={handleRowClick}
      onKeyDown={(event) => {
        if (event.key !== "Enter" && event.key !== " ") return;
        if (isInteractiveTarget(event.target)) return;
        event.preventDefault();
        onTogglePdf(attachment.id);
      }}
    >
      <div className="p-3">
        {attachment.document_type === "lab_invoice_batch" && (
          <div className="mb-2 rounded-md border border-palette-rose-border bg-palette-rose-soft px-3 py-2 text-xs text-palette-rose-heading">
            This PDF looks like a <span className="font-semibold">batch</span> (multiple invoices).
            Please upload one invoice per PDF — split it and re-upload.
          </div>
        )}
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="truncate text-sm font-medium text-palette-slate-primary">
                {attachment.original_filename}
              </span>
              {showAmount && !pdfEditing && (
                <span className="rounded bg-palette-emerald-muted px-2 py-0.5 text-xs font-medium text-palette-emerald-text">
                  {formatMoney(suggestedAmount)}
                </span>
              )}
              {!showAmount && !pdfEditing && (
                <span className="rounded bg-palette-amber-muted px-2 py-0.5 text-xs font-medium text-palette-amber-text">
                  No total
                </span>
              )}
              {assignedDateLabel && (
                <span className="rounded bg-palette-indigo-muted px-2 py-0.5 text-xs font-medium text-palette-indigo-heading">
                  Delivery {assignedDateLabel}
                </span>
              )}
              {attachment.reconciliation_status && (
                <span
                  className={`group relative inline-flex rounded px-2 py-0.5 text-xs font-medium ${getReconciliationBadgeClass(
                    attachment.reconciliation_status,
                  )}`}
                  tabIndex={mismatchDetail ? 0 : undefined}
                >
                  {attachment.reconciliation_status}
                  {mismatchDetail && (
                    <span className="pointer-events-none absolute left-0 top-full z-20 mt-1 hidden w-max max-w-[280px] rounded-md border border-palette-slate-divider bg-white px-2 py-1 text-left text-[11px] font-medium normal-case text-palette-slate-heading shadow-lg group-hover:block group-focus:block">
                      {mismatchDetail}
                    </span>
                  )}
                </span>
              )}
              {reviewRequested && (
                <span className="rounded bg-palette-rose-muted px-2 py-0.5 text-xs font-medium text-palette-rose-heading">
                  Admin review requested
                </span>
              )}
              {hasInactiveAssignedPractitioner && (
                <span className="rounded bg-palette-amber-muted px-2 py-0.5 text-xs font-medium text-palette-amber-text">
                  Inactive practitioner
                </span>
              )}
              {hasLabCostDisabledPractitioner && (
                <span className="rounded bg-palette-amber-muted px-2 py-0.5 text-xs font-medium text-palette-amber-text">
                  Lab Costs disabled
                </span>
              )}
            </div>
            <div className="mt-1 text-xs text-palette-slate-secondary">
              From {senderLabel}
              {practitionerLabel ? ` · ${practitionerLabel}` : ""}
            </div>
            {(attachment.review_reasons || []).length > 0 && (
              <div className="mt-1 flex flex-wrap gap-1">
                {attachment.review_reasons.map((reason) => (
                  <span
                    key={reason}
                    className="rounded bg-palette-amber-soft px-1.5 py-0.5 text-[11px] font-medium text-palette-amber-heading ring-1 ring-inset ring-palette-amber-border"
                  >
                    {reason}
                  </span>
                ))}
              </div>
            )}
            {hasInactiveAssignedPractitioner && (
              <div className="mt-2 rounded-md border border-palette-amber-border bg-palette-amber-soft px-2 py-1 text-xs text-palette-amber-text">
                Selected practitioner is inactive or not in the active roster. Update delivery
                before using Daily Financial Reporting.
              </div>
            )}
            {hasLabCostDisabledPractitioner && (
              <div className="mt-2 rounded-md border border-palette-amber-border bg-palette-amber-soft px-2 py-1 text-xs text-palette-amber-text">
                Selected practitioner cannot use Lab Costs in Daily Financial Reporting. Update
                delivery to a practitioner with Lab Costs enabled.
              </div>
            )}
            {canEdit && !rowLocked && assignBlockReason && (
              <div className="mt-2 rounded-md border border-palette-amber-border bg-palette-amber-soft px-2 py-1 text-xs text-palette-amber-text">
                {assignBlockReason}
              </div>
            )}
          </div>

          <div className="flex shrink-0 items-center gap-1">
            {canEdit && !rowLocked && !pdfEditing && (
              <Button
                type="button"
                variant="primary"
                size="sm"
                disabled={isScanning || isRescanning || isRetrying}
                onClick={openPdfEdit}
              >
                Edit PDF
              </Button>
            )}

            {canEdit && canRescan && canViewPdf && (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={rowLocked || isRescanning || isRetrying || isScanning}
                onClick={() => onRescan(attachment.id)}
              >
                {isRescanning ? "Rescanning..." : "Rescan"}
              </Button>
            )}
            {canEdit && canRetryFromEmail && (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={rowLocked || isRetrying || isRescanning || isScanning}
                onClick={() => onRetryFromEmail(attachment.id, attachment.original_filename)}
              >
                {isRetrying ? "Retrying..." : "Retry from email"}
              </Button>
            )}
            {/* Discard is allowed at any status — including delivered and applied — so it sits at
                row level rather than inside the Edit PDF editor, which stays locked for applied
                invoices (the pdf-edits endpoint rejects them). Discarding clears the delivery. */}
            {canEdit && (
              <Button
                type="button"
                variant="danger"
                size="sm"
                disabled={isDiscarding || isScanning || isRescanning || isRetrying}
                onClick={discard}
              >
                {isDiscarding ? "Discarding..." : "Discard"}
              </Button>
            )}
            {canEdit && pdfEditing && !rowLocked && (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={isRequestingReview || isScanning}
                onClick={requestReview}
              >
                {isRequestingReview ? "Requesting..." : "Request admin review"}
              </Button>
            )}
            {pdfEditing && (
              <>
                <Button
                  type="button"
                  variant="success"
                  size="sm"
                  disabled={isSavingPdf}
                  onClick={savePdfEdits}
                >
                  {isSavingPdf ? "Saving..." : "Save Edits"}
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  disabled={isSavingPdf}
                  onClick={cancelPdfEdit}
                >
                  Cancel
                </Button>
              </>
            )}
          </div>
        </div>

        {canAssignToReport && (
          <div className="mt-3 flex flex-wrap items-end justify-between gap-3 rounded-md border border-palette-indigo-muted bg-palette-indigo-soft/60 px-3 py-2">
            <label className="min-w-0 flex-1 text-xs font-medium text-palette-slate-badge">
              Practitioner
              <select
                className="mt-1 block w-full rounded border border-palette-slate-ring px-2 py-1.5 text-sm"
                value={draft.practitioner_id}
                disabled={assignmentDisabled}
                onChange={(event) =>
                  setDraft((current) => ({ ...current, practitioner_id: event.target.value }))
                }
              >
                <option value="">Select practitioner</option>
                {draft.practitioner_id && !selectedPractitionerInRoster && (
                  <option value={draft.practitioner_id} disabled>
                    {attachment.practitioner_name || "Inactive practitioner"} (inactive)
                  </option>
                )}
                {selectedPractitionerInRoster &&
                  !isActivePractitioner(selectedPractitionerInRoster) && (
                    <option value={draft.practitioner_id} disabled>
                      {getPractitionerName(selectedPractitionerInRoster) ||
                        attachment.practitioner_name ||
                        "Inactive practitioner"}{" "}
                      (inactive)
                    </option>
                  )}
                {selectedPractitionerInRoster &&
                  isActivePractitioner(selectedPractitionerInRoster) &&
                  !canPractitionerUseLabCosts(selectedPractitionerInRoster, hiddenColumns) && (
                    <option value={draft.practitioner_id} disabled>
                      {getPractitionerName(selectedPractitionerInRoster) ||
                        attachment.practitioner_name ||
                        "Lab Costs disabled practitioner"}{" "}
                      (Lab Costs disabled)
                    </option>
                  )}
                {assignablePractitioners.map((practitioner) => (
                  <option key={practitioner.id} value={practitioner.id}>
                    {getPractitionerName(practitioner)}
                  </option>
                ))}
              </select>
            </label>
            <Button
              type="button"
              variant="success"
              size="sm"
              disabled={assignmentDisabled || !draft.practitioner_id}
              onClick={openAssign}
            >
              {isScanning || isRescanning
                ? "Scanning..."
                : updateAssignment.isPending
                  ? "Updating..."
                  : isAssigned
                    ? "Update delivery"
                    : "Delivered"}
            </Button>
          </div>
        )}
      </div>

      {(orderedBreakdowns.length > 0 || (canEdit && !rowLocked && pdfEditing)) && (
        <div
          className="border-t border-palette-slate-divider bg-palette-slate-soft px-3 pb-3"
          data-no-row-preview="true"
        >
          <button
            type="button"
            data-no-row-preview="true"
            onClick={(event) => {
              event.stopPropagation();
              setBreakdownsOpen((open) => !open);
            }}
            className="flex w-full min-w-0 items-center gap-1 py-2 text-left text-xs font-semibold uppercase text-palette-slate-secondary hover:text-palette-slate-heading"
          >
            <span>{breakdownsOpen ? "▾" : "▸"}</span>
            Cost breakdowns
            <span className="ml-1 normal-case text-palette-slate-placeholder">
              ({orderedBreakdowns.length})
            </span>
          </button>
          {breakdownsOpen && (
            <>
              {orderedBreakdowns.map((breakdown) => (
                <BreakdownEditorRow
                  key={breakdown.id}
                  breakdown={breakdown}
                  draft={breakdownDrafts[breakdown.id] || getBreakdownDraft(breakdown)}
                  canEdit={canEdit && !reviewRequested && pdfEditing}
                  isSaving={isSavingPdf}
                  isMarkedForRemoval={removeBreakdownIds.includes(breakdown.id)}
                  onChange={updateBreakdownDraft}
                  onToggleRemove={toggleRemoveBreakdown}
                  onReview={
                    canEdit && !reviewRequested
                      ? (breakdownId) => onReviewBreakdown(attachment.id, breakdownId)
                      : null
                  }
                  isReviewing={reviewingBreakdownIds.includes(breakdown.id)}
                />
              ))}
              {canEdit && !rowLocked && pdfEditing && (
                <div className="flex justify-end border-t border-palette-slate-muted pt-3">
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => onAddBreakdown(attachment.id)}
                  >
                    +
                  </Button>
                </div>
              )}
              {pdfEditing && (
                <label className="mt-3 flex items-center justify-end gap-2 border-t border-palette-slate-muted pt-3 text-xs font-medium text-palette-slate-badge">
                  PDF total
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    className="h-8 w-28 rounded border border-palette-slate-ring px-2 text-sm"
                    value={totalDraft}
                    disabled={isSavingPdf}
                    onChange={(event) => setTotalDraft(event.target.value)}
                  />
                </label>
              )}
            </>
          )}
        </div>
      )}

      <Modal
        isOpen={assignOpen}
        onClose={() => setAssignOpen(false)}
        title={isAssigned ? "Update delivery date" : "Set delivery date"}
        size="sm"
        footer={
          <>
            <Button
              type="button"
              variant="outline"
              onClick={() => setAssignOpen(false)}
              disabled={updateAssignment.isPending}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="success"
              onClick={assign}
              disabled={updateAssignment.isPending || !assignDate}
            >
              {updateAssignment.isPending ? "Updating..." : "Delivered"}
            </Button>
          </>
        }
      >
        <p className="mb-3 text-sm text-palette-slate-badge">
          Select which report date to use for delivery of{" "}
          <span className="font-medium text-palette-slate-primary">
            {attachment.original_filename}
          </span>
          .
        </p>
        <label className="block text-xs font-medium text-palette-slate-badge">
          Delivery date
          <input
            type="date"
            className="mt-1 block w-full rounded border border-palette-slate-ring px-2 py-1 text-sm"
            value={assignDate}
            onChange={(event) => setAssignDate(event.target.value)}
          />
        </label>
      </Modal>
    </div>
  );
}

export default function LabInvoicesTab({
  clinicId,
  defaultDate,
  practitionersData,
  canEdit = true,
  canRescan = false,
  hiddenColumns = [],
}) {
  const [openAttachmentId, setOpenAttachmentId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [discardingIds, setDiscardingIds] = useState([]);
  const [discardConfirm, setDiscardConfirm] = useState(null);
  const [reviewConfirm, setReviewConfirm] = useState(null);
  const [draftBreakdowns, setDraftBreakdowns] = useState([]);
  const [duplicateClaimCandidate, setDuplicateClaimCandidate] = useState(null);
  const [savingPdfIds, setSavingPdfIds] = useState([]);
  const [requestingReviewIds, setRequestingReviewIds] = useState([]);
  const [reviewingBreakdownIds, setReviewingBreakdownIds] = useState([]);
  const [optimisticReviewedBreakdownIds, setOptimisticReviewedBreakdownIds] = useState([]);
  const [rescanningIds, setRescanningIds] = useState([]);
  const [retryingIds, setRetryingIds] = useState([]);
  const [retryConfirm, setRetryConfirm] = useState(null);
  const [activeStatusTab, setActiveStatusTab] = useState("pending");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [lastStatusCounts, setLastStatusCounts] = useState(null);
  const [lastAllDatesPendingCount, setLastAllDatesPendingCount] = useState(null);
  const [lastAmountTotals, setLastAmountTotals] = useState(null);
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(searchQuery);
  const updateAssignment = useUpdateLabInvoiceAttachmentAssignment();
  const discardAttachment = useDiscardLabInvoiceAttachment();
  const requestReview = useRequestLabInvoiceAttachmentReview();
  const reviewBreakdown = useReviewLabInvoiceBreakdown();
  const claimAttachment = useClaimLabInvoiceAttachment();
  const rescanAttachment = useRescanLabInvoiceAttachment();
  const retryFromEmail = useRetryLabInvoiceAttachmentFromEmail();
  const updatePdfEdits = useUpdateLabInvoiceAttachmentPdfEdits();
  const uploadLabInvoice = useUploadLabInvoice();
  const fileInputRef = useRef(null);
  const effectiveMonth = useMemo(() => String(defaultDate || "").slice(0, 7), [defaultDate]);
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [searchQuery]);

  const isPendingTab = activeStatusTab === "pending";
  const labInvoiceFilters = useMemo(
    () => ({
      clinic: clinicId,
      // Pending inbox is all-dates; other status tabs stay scoped to the month in the URL/date picker.
      effective_month: isPendingTab ? undefined : effectiveMonth,
      status: activeStatusTab,
      page,
      page_size: pageSize,
      q: debouncedSearchQuery || undefined,
    }),
    [clinicId, effectiveMonth, activeStatusTab, isPendingTab, page, pageSize, debouncedSearchQuery],
  );
  // Badge scopes are deliberately mixed: Pending is an all-dates inbox, while All/Delivered/
  // Applied stay scoped to the month in the URL/date picker. The main list query can only carry
  // one of those two scopes, so this companion query always carries the OTHER one. That is what
  // keeps every badge meaning the same thing on every tab — switching tabs must never move a
  // number. Only ever fetches one row (`page_size: 1`); we use it purely for `status_counts`.
  const counterpartCountFilters = useMemo(
    () => ({
      clinic: clinicId,
      // Pending tab → main query is all-dates, so fetch the month-scoped counts here.
      // Any other tab → main query is month-scoped, so fetch the all-dates counts here.
      effective_month: isPendingTab ? effectiveMonth : undefined,
      status: "all",
      page: 1,
      page_size: 1,
    }),
    [clinicId, effectiveMonth, isPendingTab],
  );
  const {
    data: labInvoiceData,
    isLoading: labInvoiceLoading,
    isError: labInvoiceIsError,
    error: labInvoiceError,
  } = useLabInvoiceAttachments(labInvoiceFilters);
  const { data: counterpartCountData } = useLabInvoiceAttachments(counterpartCountFilters, {
    // The all-dates variant needs no month, so only the month-scoped one waits for it.
    enabled: Boolean(clinicId) && (!isPendingTab || Boolean(effectiveMonth)),
  });

  useEffect(() => {
    setPage(1);
  }, [debouncedSearchQuery, activeStatusTab, effectiveMonth]);

  useEffect(() => {
    setLastStatusCounts(null);
    setLastAmountTotals(null);
  }, [clinicId, effectiveMonth]);

  // The all-dates pending count does not depend on the month, so it only resets per clinic.
  useEffect(() => {
    setLastAllDatesPendingCount(null);
  }, [clinicId]);

  const attachments = useMemo(() => labInvoiceData?.results || [], [labInvoiceData?.results]);
  const initialLabInvoiceLoading = labInvoiceLoading && !labInvoiceData;
  const attachmentsWithDrafts = useMemo(
    () =>
      attachments.map((attachment) => {
        const reviewedAttachment = withOptimisticReviewedBreakdowns(
          attachment,
          optimisticReviewedBreakdownIds,
        );
        return {
          ...reviewedAttachment,
          breakdowns: [
            ...(reviewedAttachment.breakdowns || []),
            ...draftBreakdowns.filter((breakdown) => breakdown.attachment === attachment.id),
          ],
        };
      }),
    [attachments, draftBreakdowns, optimisticReviewedBreakdownIds],
  );
  // Whichever of the two queries is carrying each scope on this tab. Everything below reads the
  // counts through these, so no badge is derived from `activeStatusTab`.
  const monthScopedCountSource = isPendingTab ? counterpartCountData : labInvoiceData;
  const allDatesCountSource = isPendingTab ? labInvoiceData : counterpartCountData;

  useEffect(() => {
    if (monthScopedCountSource?.status_counts) {
      setLastStatusCounts(monthScopedCountSource.status_counts);
    }
  }, [monthScopedCountSource?.status_counts]);
  useEffect(() => {
    const pending = allDatesCountSource?.status_counts?.pending;
    if (pending !== undefined && pending !== null) {
      setLastAllDatesPendingCount(pending);
    }
  }, [allDatesCountSource?.status_counts?.pending]);
  useEffect(() => {
    // Amount totals follow the active tab's own scope (the summary label states which), so only
    // the month-scoped tabs seed the fallback.
    if (isPendingTab) return;
    if (labInvoiceData?.amount_totals) {
      setLastAmountTotals(labInvoiceData.amount_totals);
    }
  }, [labInvoiceData?.amount_totals, isPendingTab]);

  const monthScopedCounts = monthScopedCountSource?.status_counts || lastStatusCounts || {};
  // Pending is always the all-dates inbox count; the other three are always month-scoped. This
  // shape is unconditional, so changing tabs cannot change a badge — it only changes which query
  // supplies it, and both queries are kept alive.
  const statusCounts = {
    all: monthScopedCounts.all ?? 0,
    delivered: monthScopedCounts.delivered ?? 0,
    applied: monthScopedCounts.applied ?? 0,
    pending: allDatesCountSource?.status_counts?.pending ?? lastAllDatesPendingCount ?? 0,
  };
  const amountTotals = isPendingTab
    ? labInvoiceData?.amount_totals || {}
    : labInvoiceData?.amount_totals || lastAmountTotals || {};
  const activeStatusLabel =
    LAB_INVOICE_STATUS_TABS.find((tab) => tab.key === activeStatusTab)?.label || "Monthly";
  const activeMonthlyTotal = amountTotals[activeStatusTab] ?? 0;
  const labInvoiceScopeLabel = isPendingTab ? "All pending / undelivered PDFs" : effectiveMonth;
  const summaryScopeLabel = isPendingTab ? "All dates" : effectiveMonth;
  const summaryTotalLabel = isPendingTab
    ? "Pending Undelivered Total"
    : `${activeStatusLabel} Monthly Total`;
  const practitioners = useMemo(() => getRoster(practitionersData), [practitionersData]);
  const openAttachment = useMemo(
    () =>
      openAttachmentId == null
        ? null
        : [
            ...attachmentsWithDrafts,
            ...(duplicateClaimCandidate?.attachment ? [duplicateClaimCandidate.attachment] : []),
          ].find((item) => item.id === openAttachmentId) || null,
    [openAttachmentId, attachmentsWithDrafts, duplicateClaimCandidate],
  );

  function togglePdf(id) {
    setOpenAttachmentId((current) => (current === id ? null : id));
  }

  function triggerUpload() {
    fileInputRef.current?.click();
  }

  async function handleFileSelected(event) {
    const file = event.target.files?.[0];
    event.target.value = ""; // allow re-selecting the same file
    if (!file) return;

    const isPdf = file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
    if (!isPdf) {
      showError("Only PDF files can be uploaded.");
      return;
    }
    if (file.size > MAX_PDF_UPLOAD_BYTES) {
      showError("PDF is too large. Maximum file size is 2 MB.");
      return;
    }

    try {
      const data = await uploadLabInvoice.mutateAsync({ file, clinic: clinicId });
      if (data?.duplicate) {
        if (data.can_claim && data.attachment) {
          setDuplicateClaimCandidate(data);
          setOpenAttachmentId(data.attachment.id);
          showSuccess("This PDF already exists. Claim it to this clinic below.");
        } else {
          showError(
            data.claim_error ||
              data.detail ||
              "This PDF was already received and cannot be claimed from this clinic.",
          );
        }
      } else {
        setDuplicateClaimCandidate(null);
        showSuccess("Uploaded — scanning…");
      }
    } catch (err) {
      showError(getApiErrorMessage(err, "Failed to upload lab invoice."));
    }
  }

  async function claimDuplicateAttachment() {
    const attachmentId =
      duplicateClaimCandidate?.attachment_id || duplicateClaimCandidate?.attachment?.id;
    if (!attachmentId) return;
    try {
      const attachment = await claimAttachment.mutateAsync({ attachmentId, clinic: clinicId });
      setDuplicateClaimCandidate(null);
      setOpenAttachmentId(attachment.id);
      showSuccess("Existing PDF claimed to this clinic.");
    } catch (err) {
      showError(getApiErrorMessage(err, "Failed to claim existing PDF."));
    }
  }

  function requestDiscard(id, filename, meta = {}) {
    setDiscardConfirm({
      id,
      filename: filename || "",
      delivered: Boolean(meta.delivered),
      applied: Boolean(meta.applied),
    });
  }

  function requestAdminReview(id, filename) {
    setReviewConfirm({ id, filename: filename || "" });
  }

  async function confirmRequestAdminReview() {
    if (!reviewConfirm) return;
    const id = reviewConfirm.id;
    setReviewConfirm(null);
    setRequestingReviewIds((current) => (current.includes(id) ? current : [...current, id]));
    try {
      await requestReview.mutateAsync(id);
      showSuccess("Admin review requested.");
    } catch (err) {
      showError(getApiErrorMessage(err, "Failed to request admin review."));
    } finally {
      setRequestingReviewIds((current) => current.filter((item) => item !== id));
    }
  }

  async function markBreakdownReviewed(attachmentId, breakdownId) {
    setReviewingBreakdownIds((current) =>
      current.includes(breakdownId) ? current : [...current, breakdownId],
    );
    try {
      await reviewBreakdown.mutateAsync({ attachmentId, breakdownId });
      setOptimisticReviewedBreakdownIds((current) =>
        current.includes(breakdownId) ? current : [...current, breakdownId],
      );
      showSuccess("Breakdown row reviewed.");
    } catch (err) {
      showError(getApiErrorMessage(err, "Failed to review breakdown row."));
    } finally {
      setReviewingBreakdownIds((current) => current.filter((item) => item !== breakdownId));
    }
  }

  async function confirmDiscard() {
    if (!discardConfirm) return;
    const id = discardConfirm.id;
    setDiscardConfirm(null);
    setDiscardingIds((current) => (current.includes(id) ? current : [...current, id]));
    try {
      await discardAttachment.mutateAsync(id);
      if (openAttachmentId === id) setOpenAttachmentId(null);
      showSuccess("Lab invoice discarded.");
    } catch (err) {
      showError(getApiErrorMessage(err, "Failed to discard lab invoice."));
    } finally {
      setDiscardingIds((current) => current.filter((item) => item !== id));
    }
  }

  async function rescan(id) {
    setRescanningIds((current) => (current.includes(id) ? current : [...current, id]));
    try {
      await rescanAttachment.mutateAsync(id);
      showSuccess("Lab invoice rescanned.");
    } catch (err) {
      showError(getApiErrorMessage(err, "Failed to rescan lab invoice."));
    } finally {
      setRescanningIds((current) => current.filter((item) => item !== id));
    }
  }

  function requestRetryFromEmail(id, filename) {
    setRetryConfirm({ id, filename: filename || "" });
  }

  async function confirmRetryFromEmail() {
    if (!retryConfirm) return;
    const id = retryConfirm.id;
    setRetryConfirm(null);
    setRetryingIds((current) => (current.includes(id) ? current : [...current, id]));
    try {
      await retryFromEmail.mutateAsync(id);
      showSuccess("Retried from email — re-fetching and scanning.");
    } catch (err) {
      showError(getApiErrorMessage(err, "Failed to retry from email."));
    } finally {
      setRetryingIds((current) => current.filter((item) => item !== id));
    }
  }

  async function savePdfEdits({
    attachmentId,
    totalAmount,
    breakdowns,
    deleteBreakdownIds = [],
    discardDraftIds = [],
  }) {
    setSavingPdfIds((current) =>
      current.includes(attachmentId) ? current : [...current, attachmentId],
    );
    const draftIdsToClear = [...discardDraftIds];
    try {
      const payloadBreakdowns = breakdowns.filter((breakdown) => {
        if (!isDraftBreakdownId(breakdown.id)) return true;
        if (!draftIdsToClear.includes(breakdown.id)) draftIdsToClear.push(breakdown.id);
        return !isBlankBreakdownDraft(breakdown.data);
      });
      await updatePdfEdits.mutateAsync({
        attachmentId,
        data: {
          total_amount: totalAmount,
          breakdowns: payloadBreakdowns,
          delete_breakdown_ids: deleteBreakdownIds,
        },
      });
      if (draftIdsToClear.length > 0) {
        setDraftBreakdowns((current) =>
          current.filter((breakdown) => !draftIdsToClear.includes(breakdown.id)),
        );
      }
      showSuccess("PDF edits saved.");
    } catch (err) {
      showError(getApiErrorMessage(err, "Failed to save PDF edits."));
      throw err;
    } finally {
      setSavingPdfIds((current) => current.filter((item) => item !== attachmentId));
    }
  }

  function cancelPdfEdits(attachmentId) {
    setDraftBreakdowns((current) =>
      current.filter((breakdown) => breakdown.attachment !== attachmentId),
    );
  }

  function addBreakdown(attachmentId) {
    setDraftBreakdowns((current) => [...current, createDraftBreakdown(attachmentId)]);
  }

  return (
    <div className="mt-6 space-y-4">
      <div className="rounded-md border border-palette-slate-divider bg-white p-3">
        <label className="block text-xs font-medium text-palette-slate-badge">
          Search by amount or practitioner
          <div className="mt-1 flex items-center gap-2">
            <input
              type="text"
              placeholder="e.g. 490 or Doctor"
              className="block w-full max-w-sm rounded border border-palette-slate-ring px-2 py-1 text-sm"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
            />
            {searchQuery && (
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  setSearchQuery("");
                  setDebouncedSearchQuery("");
                  setPage(1);
                }}
              >
                Clear
              </Button>
            )}
          </div>
        </label>
      </div>

      <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
        <div className="min-w-0 flex-1">
          <section className="rounded-md border border-palette-slate-divider bg-palette-slate-muted p-4">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <div>
                <h3 className="text-sm font-semibold text-palette-slate-primary">Lab Invoices</h3>
                <p className="text-xs text-palette-slate-secondary">
                  {clinicId} - {labInvoiceScopeLabel}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {labInvoiceLoading && labInvoiceData && (
                  <span className="text-xs text-palette-slate-secondary">Refreshing...</span>
                )}
                {canEdit && (
                  <>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="application/pdf,.pdf"
                      className="hidden"
                      onChange={handleFileSelected}
                    />
                    <Button
                      type="button"
                      variant="primary"
                      size="sm"
                      disabled={uploadLabInvoice.isPending}
                      onClick={triggerUpload}
                    >
                      {uploadLabInvoice.isPending ? "Uploading..." : "Upload PDF"}
                    </Button>
                  </>
                )}
              </div>
            </div>

            <div className="mb-4 flex items-center gap-8 rounded-lg border border-palette-gray-subtle bg-palette-gray-soft px-5 py-3">
              <p className="whitespace-nowrap text-sm font-medium text-palette-gray-secondary">
                {clinicId} · {summaryScopeLabel}
              </p>
              <div className="self-stretch border-l border-palette-gray-divider" />
              <div>
                <p className="mb-0.5 text-xs uppercase tracking-wide text-palette-gray-secondary">
                  {summaryTotalLabel}
                </p>
                <p className="text-base font-semibold text-palette-indigo-badge">
                  {formatMoney(activeMonthlyTotal)}
                </p>
              </div>
            </div>

            <div className="mb-4 flex flex-wrap gap-1 border-b border-palette-slate-divider">
              {LAB_INVOICE_STATUS_TABS.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setActiveStatusTab(tab.key)}
                  className={`-mb-px border-b-2 px-3 py-1.5 text-sm font-medium ${
                    activeStatusTab === tab.key
                      ? "border-palette-indigo-label text-palette-indigo-badge"
                      : "border-transparent text-palette-slate-secondary hover:text-palette-slate-heading"
                  }`}
                >
                  {tab.label} ({statusCounts[tab.key] ?? 0})
                </button>
              ))}
            </div>

            {labInvoiceIsError && (
              <div className="text-sm text-palette-rose-badge">
                {getApiErrorMessage(labInvoiceError, "Unable to load lab invoices.")}
              </div>
            )}

            {initialLabInvoiceLoading && (
              <Loading size="sm" text="Loading lab invoices..." className="py-6" />
            )}

            {duplicateClaimCandidate?.attachment && (
              <div
                className={`mb-3 cursor-pointer rounded-md border bg-palette-amber-soft p-3 ${
                  openAttachmentId === duplicateClaimCandidate.attachment.id
                    ? "border-palette-indigo-ring ring-1 ring-palette-indigo-hover"
                    : "border-palette-amber-border hover:border-palette-amber-ring"
                }`}
                role="button"
                tabIndex={0}
                onClick={() => setOpenAttachmentId(duplicateClaimCandidate.attachment.id)}
                onKeyDown={(event) => {
                  if (event.key !== "Enter" && event.key !== " ") return;
                  event.preventDefault();
                  setOpenAttachmentId(duplicateClaimCandidate.attachment.id);
                }}
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-xs font-semibold uppercase text-palette-amber-heading">
                      Existing PDF found
                    </div>
                    <div className="mt-1 truncate text-sm font-medium text-palette-slate-primary">
                      {duplicateClaimCandidate.attachment.original_filename}
                    </div>
                    <div className="mt-1 text-xs text-palette-slate-badge">
                      {duplicateClaimCandidate.detail ||
                        "This PDF is already in the system but is not linked to this clinic."}
                    </div>
                    {duplicateClaimCandidate.attachment.ai_error && (
                      <div className="mt-1 text-xs text-palette-rose-heading">
                        AI error: {duplicateClaimCandidate.attachment.ai_error}
                      </div>
                    )}
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={(event) => {
                        event.stopPropagation();
                        setOpenAttachmentId(duplicateClaimCandidate.attachment.id);
                      }}
                    >
                      Preview
                    </Button>
                    <Button
                      type="button"
                      variant="success"
                      size="sm"
                      disabled={claimAttachment.isPending}
                      onClick={(event) => {
                        event.stopPropagation();
                        claimDuplicateAttachment();
                      }}
                    >
                      {claimAttachment.isPending ? "Claiming..." : `Claim to ${clinicId}`}
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      disabled={claimAttachment.isPending}
                      onClick={(event) => {
                        event.stopPropagation();
                        setDuplicateClaimCandidate(null);
                      }}
                    >
                      Dismiss
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {!duplicateClaimCandidate?.attachment &&
              !initialLabInvoiceLoading &&
              !labInvoiceIsError &&
              attachmentsWithDrafts.length === 0 && (
                <div className="text-sm text-palette-slate-secondary">
                  {getLabInvoiceEmptyMessage(activeStatusTab, Boolean(searchQuery))}
                </div>
              )}

            <div className="space-y-3">
              {attachmentsWithDrafts.map((attachment) => (
                <InvoiceRow
                  key={attachment.id}
                  attachment={attachment}
                  clinicId={clinicId}
                  defaultDate={defaultDate}
                  practitioners={practitioners}
                  hiddenColumns={hiddenColumns}
                  canEdit={canEdit}
                  canRescan={canRescan}
                  isOpen={openAttachmentId === attachment.id}
                  onTogglePdf={togglePdf}
                  updateAssignment={updateAssignment}
                  onRequestReview={requestAdminReview}
                  onAddBreakdown={addBreakdown}
                  onSavePdfEdits={savePdfEdits}
                  onCancelPdfEdits={cancelPdfEdits}
                  isSavingPdf={savingPdfIds.includes(attachment.id)}
                  isRequestingReview={requestingReviewIds.includes(attachment.id)}
                  onDiscard={requestDiscard}
                  isDiscarding={discardingIds.includes(attachment.id)}
                  onRescan={rescan}
                  isRescanning={rescanningIds.includes(attachment.id)}
                  onRetryFromEmail={requestRetryFromEmail}
                  isRetrying={retryingIds.includes(attachment.id)}
                  onReviewBreakdown={markBreakdownReviewed}
                  reviewingBreakdownIds={reviewingBreakdownIds}
                />
              ))}
            </div>

            {(labInvoiceData?.count || 0) > 0 && (
              <div className="mt-3 rounded-md border border-palette-slate-divider bg-white">
                <Pagination
                  page={labInvoiceData?.page || page}
                  pageSize={labInvoiceData?.page_size || pageSize}
                  total={labInvoiceData?.count || 0}
                  onPageChange={setPage}
                  onPageSizeChange={(size) => {
                    setPage(1);
                    setPageSize(size);
                  }}
                  pageSizeOptions={[10, 20, 50]}
                />
              </div>
            )}
          </section>
        </div>

        <div className="w-full shrink-0 lg:sticky lg:top-4 lg:w-[45%] lg:max-w-[680px]">
          {openAttachment ? (
            <div className="overflow-hidden rounded-md border border-palette-slate-divider bg-white">
              <div className="flex items-center justify-between gap-2 border-b border-palette-slate-divider px-3 py-2">
                <span className="truncate text-sm font-medium text-palette-slate-primary">
                  {openAttachment.original_filename}
                </span>
                <Button type="button" variant="secondary" onClick={() => setOpenAttachmentId(null)}>
                  Close
                </Button>
              </div>
              <div className="h-[80vh] overflow-hidden bg-palette-slate-muted">
                <LabInvoicePdfFrame attachmentId={openAttachment.id} className="min-h-full" />
              </div>
            </div>
          ) : (
            <div className="flex h-[80vh] items-center justify-center rounded-md border border-dashed border-palette-slate-ring bg-white text-sm text-palette-slate-placeholder">
              Select a lab invoice row to preview the PDF here.
            </div>
          )}
        </div>
      </div>

      <ConfirmModal
        isOpen={Boolean(discardConfirm)}
        onClose={() => setDiscardConfirm(null)}
        onConfirm={confirmDiscard}
        title="Discard lab invoice?"
        message={
          <div className="space-y-2">
            <p>
              {discardConfirm?.filename
                ? `Discard "${discardConfirm.filename}"? It will no longer appear on this tab.`
                : "Discard this lab invoice? It will no longer appear on this tab."}
            </p>
            {discardConfirm?.delivered && <p>Its delivery date will be cleared.</p>}
            {discardConfirm?.applied && (
              <p className="font-medium text-palette-rose-heading">
                This invoice is already applied to a submitted Daily Report. That report&apos;s lab
                cost stays unchanged, but the PDF will drop out of the monthly compensation PDF
                breakdown and the month-end download packet.
              </p>
            )}
          </div>
        }
        confirmText="Discard"
        confirmVariant="danger"
      />

      <ConfirmModal
        isOpen={Boolean(reviewConfirm)}
        onClose={() => setReviewConfirm(null)}
        onConfirm={confirmRequestAdminReview}
        title="Request admin review?"
        message={
          <div className="space-y-2">
            <p>Found something wrong with this lab invoice?</p>
            <p>
              Send it to admin review. Admin will check the clinic, date, practitioner, and invoice
              details before delivery.
            </p>
            {reviewConfirm?.filename && (
              <p className="font-medium text-palette-slate-primary">{reviewConfirm.filename}</p>
            )}
          </div>
        }
        confirmText="Send to admin"
        confirmVariant="primary"
      />

      <ConfirmModal
        isOpen={Boolean(retryConfirm)}
        onClose={() => setRetryConfirm(null)}
        onConfirm={confirmRetryFromEmail}
        title="Retry from email?"
        message={
          retryConfirm?.filename
            ? `Re-fetch "${retryConfirm.filename}" from the original email and scan it again?`
            : "Re-fetch this attachment from the original email and scan it again?"
        }
        confirmText="Retry"
        confirmVariant="primary"
      />
    </div>
  );
}
